import React from 'react';
// template components
import Accordion from '@nmx/template/dist/componentLibrary/modules/Accordion';
import BulletedList from '@nmx/template/dist/componentLibrary/components/BulletedList';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// icons
import MinusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/MinusSignFill.js';
import PlusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/PlusSignFill.js';
// styles
import { DescriptionListStyled } from './styles';

export default () => (
  <SectionContainer
    id='financial-planning-faq' // '-section-container' will be appended dynamically
    className='nmx-no-print'
    moduleName='faq-module'
    moduleVariation='A'>
    <Row align='center'>
      <Col
        align='center'
        medium={10}
        large={8}>
        <Typography
          component='h2'
          nextElement='typography'>
          Questions about <WidowBlock>financial planning&#63;</WidowBlock>
          <Typography
            component='span'
            block>
            We&#39;ve got answers.
          </Typography>
        </Typography>
      </Col>
    </Row>
    <Row align='center'>
      <Col
        medium={10}
        large={8}>
        <Accordion
          id='faq-financial-planning'
          variant='secondary'
          toggleOpenIcon={PlusSignFillIcon}
          toggleCloseIcon={MinusSignFillIcon}
          titleTypographyProps={{ component: 'h3' }}
          isFAQ
          items={[
            {
              id: 'financial-planning-difference-between-planning-and-budgeting-faq',
              title: (<>What&#39;s the difference between financial planning <WidowBlock>and budgeting&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    Budgeting is a key step within financial planning. Financial planning is a comprehensive process that lays out your short&#45; and long&#45;term goals like retirement, sending your kids to college, or wealth accumulation, and developing strategies to achieve them. Your plan could include, investment strategies, tax planning, risk management, and estate planning. The focus is on creating a roadmap for sustained financial security.
                  </Typography>
                  <Typography nextElement='typography'>
                    Budgeting, on the other hand, is a tactical process that involves{' '}
                    <Link
                      id='financial-planning-how-to-create-a-monthly-budget-faq-link'
                      linkUrl='/life-and-money/how-to-create-a-monthly-budget/'
                      variant='primaryDeemphasize'>
                      creating a detailed plan for managing income and expenses
                    </Link>
                    {' '}over a specific period, usually monthly or annually. The primary goal of budgeting is to control spending, ensure sufficient cash flow, and avoid debt, thereby supporting your broader financial plan.
                  </Typography>
                  <Typography nextElement='typography'>
                    So, financial planning is strategic and long&#45;term, while budgeting is operational and short&#45;term.
                  </Typography>
                  <Typography disableBottomPadding>
                    <Link
                      id='financial-planning-budgeting-vs-financial-planning-faq-link'
                      linkUrl='/life-and-money/budgeting-vs-financial-planning/'
                      variant='primaryDeemphasize'>
                      See more differences between budgeting and financial planning
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'financial-planning-what-is-it-faq',
              title: (<>So, what exactly is financial planning with <WidowBlock>Northwestern Mutual&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  Planning with us means you have a better way to money. It starts with a conversation about your life and what&#39;s important to you. Your advisor&#39;s expertise will help reveal blind spots and identify the opportunities that others might overlook. As you work with your advisor, you can tailor your plan from our exclusive portfolio of financial products. Each is designed to meet you where you are, at every stage in your life, helping to identify and address any gaps you might have.{' '}
                  <Link
                    id='financial-planning-what-is-it-faq-link'
                    linkUrl='/life-and-money/what-is-financial-planning/'
                    variant='primaryDeemphasize'>
                    Discover more about financial planning
                  </Link>.
                </Typography>
              ),
            },
            {
              id: 'financial-planning-first-step-faq',
              title: (<>What&#39;s the first step in financial planning with <WidowBlock>Northwestern Mutual&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  First, get matched with the{' '}
                  <Link
                    id='financial-planning-first-step-faq-link'
                    linkUrl='/find-a-financial-advisor/'
                    variant='primaryDeemphasize'
                    ariaLabel='Find the right Northwestern Mutual financial advisor for you'>
                    right financial advisor for you
                  </Link>
                  . At your first meeting, your advisor will ask you deeper questions and listen closely to fully understand your goals and what&#39;s most important to you. With a holistic view of your financial picture in focus, they&#39;ll tailor your plan to your priorities. Let&#39;s get you prepared for your first meeting.{' '}
                  <Link
                    id='financial-planning-meeting-with-a-financial-advisor-what-to-bring-faq-link'
                    linkUrl='/life-and-money/meeting-with-a-financial-advisor-what-to-bring/'
                    variant='primaryDeemphasize'
                    ariaLabel='Here&#39;s what you need to know about your first meeting with a financial advisor'>
                    Here&#39;s what you need to know
                  </Link>.
                </Typography>
              ),
            },
            {
              id: 'financial-planning-is-it-expensive-faq',
              title: (<>Is financial <WidowBlock>planning expensive&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  Financial advisors make money in different ways, and often, fees depend on what you&#39;re asking your advisor to do for you. There&#39;s also the possibility that your advisor may not charge you a fee. Most of{' '}
                  <Link
                    id='financial-planning-our-advisors-faq-link'
                    linkUrl='/find-a-financial-advisor/'
                    variant='primaryDeemphasize'
                    ariaLabel='Our advisors are here to help you get started'>
                    our advisors
                  </Link>
                  {' '}will design your financial plan free of charge. However, some advisors could charge a fee for certain types of planning, but they&#39;ll let you know up front. Want to know more about how advisors make their money&#63;{' '}
                  <Link
                    id='financial-planning-how-much-does-a-financial-advisor-cost-faq-link'
                    linkUrl='/life-and-money/how-much-does-a-financial-advisor-cost/'
                    variant='primaryDeemphasize'
                    ariaLabel='This article lays everything out about how financial advisors make their money'>
                    This article lays everything out
                  </Link>.
                </Typography>
              ),
            },
            {
              id: 'financial-planning-do-i-need-a-lot-of-money-faq',
              title: (<>Do I need to have a lot of money to have a <WidowBlock>financial plan&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  Nope. You have a better way to money with us. Our holistic approach to financial planning starts with where you are, wherever that is. And the sooner you start, the bigger opportunity you&#39;ll have to set yourself up for financial success now, and later on.{' '}
                  <Link
                    id='financial-planning-do-i-need-a-lot-of-money-faq-link'
                    linkUrl='/find-a-financial-advisor/'
                    variant='primaryDeemphasize'
                    ariaLabel='Take the first step and get matched with a financial advisor.'>
                    Get matched with an advisor
                  </Link>.
                </Typography>
              ),
            },
            {
              id: 'financial-planning-why-is-it-important',
              title: (<>Why is financial <WidowBlock>planning important&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    You can do all the things that are important to you now, and down the road. Once you have your personalized financial plan from Northwestern Mutual, you can see:
                  </Typography>
                  <BulletedList
                    bulletType='filledCircle'
                    bulletedListData={[
                      { bulletText: 'What it will take to reach your goals' },
                      { bulletText: <>The progress you&#39;ve already made</> },
                      { bulletText: 'Blind spots that otherwise might have been overlooked' },
                      { bulletText: 'Opportunities you can take advantage of' },
                    ]}
                  />
                  <Typography disableBottomPadding>
                    Let&#39;s get started on your plan.{' '}
                    <Link
                      id='financial-planning-why-is-it-important-link'
                      linkUrl='/find-a-financial-advisor/'
                      variant='primaryDeemphasize'
                      ariaLabel='Find your financial advisor and learn why financial planning is important.'>
                      Find your advisor now
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'financial-planning-different-types-of-financial-planning-faq',
              title: (<>What are some different types of <WidowBlock>financial planning&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    Financial planning encompasses several types, each tailored to specific goals:
                  </Typography>
                  <DescriptionListStyled
                    descriptionListData={[
                      {
                        descriptionTerm: (
                          <Typography
                            component='span'
                            disableBottomPadding>
                            <Link
                              id='financial-planning-types-of-financial-planning-retirement-planning-faq-link'
                              linkUrl='/retirement-planning/'
                              variant='primaryDeemphasize'
                              ariaLabel='Learn more about retirement planning'>
                              Retirement Planning
                            </Link>:
                          </Typography>
                        ),
                        descriptionDetails: 'Helps make sure you have sufficient funds for a comfortable retirement through investments, savings, whole life insurance, and guaranteed income in retirement.',
                      },
                      {
                        descriptionTerm: (
                          <Typography
                            component='span'
                            disableBottomPadding>
                            <Link
                              id='financial-planning-types-of-financial-planning-investment-planning-faq-link'
                              linkUrl='/investments/'
                              variant='primaryDeemphasize'
                              ariaLabel='Learn more about investment planning'>
                              Investment Planning
                            </Link>:
                          </Typography>
                        ),
                        descriptionDetails: (
                          <>
                            Focuses on creating a diversified portfolio to help you reach your short and long&#45;term wealth accumulation goals.
                          </>
                        ),
                      },
                      {
                        descriptionTerm: (
                          <Typography
                            component='span'
                            disableBottomPadding>
                            <Link
                              id='financial-planning-types-of-financial-planning-insurance-planning-faq-link'
                              linkUrl='/insurance/'
                              variant='primaryDeemphasize'
                              ariaLabel='Learn more about insurance planning'>
                              Insurance Planning
                            </Link>:
                          </Typography>
                        ),
                        descriptionDetails: 'Assesses risk and determines the right insurance solutions to help safeguard against the unexpected.',
                      },
                      {
                        descriptionTerm: (
                          <Typography
                            component='span'
                            disableBottomPadding>
                            <Link
                              id='financial-planning-types-of-financial-planning-education-planning-faq-link'
                              linkUrl='/college-savings-plans/'
                              variant='primaryDeemphasize'
                              ariaLabel='Learn more about education planning'>
                              Education Planning
                            </Link>:
                          </Typography>
                        ),
                        descriptionDetails: (
                          <>
                            Finds ways for you to save and invest so that you can help fund a child&#39;s educational expenses.
                          </>
                        ),
                      },
                      {
                        descriptionTerm: (
                          <Typography
                            component='span'
                            disableBottomPadding>
                            <Link
                              id='financial-planning-types-of-financial-planning-ltc-planning-faq-link'
                              linkUrl='/long-term-care-planning/'
                              variant='primaryDeemphasize'
                              ariaLabel='Learn more about long&#45;term care planning'>
                              Long&#45;Term Care Planning
                            </Link>:
                          </Typography>
                        ),
                        descriptionDetails: 'Makes sure that you have a plan in place to help pay for the expenses of care if you have a chronic illness or debilitating injury that keeps you from caring for yourself.',
                      },
                      {
                        descriptionTerm: (
                          <Typography
                            component='span'
                            disableBottomPadding>
                            <Link
                              id='financial-planning-types-of-financial-planning-estate-planning-faq-link'
                              linkUrl='/financial-planning/estate-planning/'
                              variant='primaryDeemphasize'
                              ariaLabel='Learn more about estate planning'>
                              Estate Planning
                            </Link>:
                          </Typography>
                        ),
                        descriptionDetails: 'Prepares for the distribution of assets after you pass away, including wills, trusts, and inheritance planning to protect your assets to benefit your beneficiaries.',
                      },
                      {
                        descriptionTerm: (
                          <Typography
                            component='span'
                            disableBottomPadding>
                            <Link
                              id='financial-planning-types-of-financial-planning-debt-managing-planning-faq-link'
                              linkUrl='/life-and-money/everyday-money/managing-debt/'
                              variant='primaryDeemphasize'
                              ariaLabel='Learn more about debt management planning'>
                              Debt Management
                            </Link>:
                          </Typography>
                        ),
                        descriptionDetails: 'Develops strategies to help manage and reduce debt effectively to help improve your financial stability.',
                      },
                      {
                        descriptionTerm: (
                          <Typography
                            component='span'
                            disableBottomPadding>
                            <Link
                              id='financial-planning-types-of-financial-planning-tax-planning-faq-link'
                              linkUrl='/life-and-money/financial-planning/your-taxes/'
                              variant='primaryDeemphasize'
                              ariaLabel='Learn more about tax planning'>
                              Tax Planning
                            </Link>:
                          </Typography>
                        ),
                        descriptionDetails: 'Involves investment and insurance strategies to help minimize gains and losses as well as inheritance and estate taxes.',
                      },
                      {
                        descriptionTerm: (
                          <Typography
                            component='span'
                            disableBottomPadding>
                            <Link
                              id='financial-planning-types-of-financial-planning-business-planning-faq-link'
                              linkUrl='/financial-planning/business-services/'
                              variant='primaryDeemphasize'
                              ariaLabel='Learn more about business planning'>
                              Business Planning
                            </Link>:
                          </Typography>
                        ),
                        descriptionDetails: 'Creates strategies for growth, operational continuity, future leaders, business succession, risk management, and personal wealth.',
                      },
                    ]}
                    disableBullet
                  />
                  <Typography nextElement='typography'>
                    Each type addresses different aspects of financial well&#45;being, contributing to a comprehensive financial strategy.{' '}
                    <Link
                      id='financial-planning-what-is-included-in-a-financial-plan-faq-link'
                      linkUrl='/life-and-money/what-is-included-in-a-financial-plan/'
                      variant='primaryDeemphasize'>
                      Find out the must have components of a good financial plan
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'financial-planning-benefits-faq',
              title: (<>What are the benefits of <WidowBlock>financial planning&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  One of the biggest benefits of your Northwestern Mutual financial plan is that it can give you the confidence to do the things you&#39;ve always wanted to&#8212;without worrying if you can afford to. In fact, independent analysis has shown that clients who follow a comprehensive approach to planning experienced better outcomes.<sup>1</sup> So you&#39;ll be set up for success from the start. Let&#39;s get started on your plan.{' '}
                  <Link
                    id='financial-planning-benefits-faq-link'
                    linkUrl='/find-a-financial-advisor/'
                    variant='primaryDeemphasize'
                    ariaLabel='Find your financial advisor now and learn more about the benefits of financial planning.'>
                    Find your advisor now
                  </Link>.
                </Typography>
              ),
            },
            {
              id: 'financial-planning-does-nm-offer-private-wealth-management-faq',
              title: (<>Does Northwestern Mutual offer private <WidowBlock>wealth management&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  Absolutely. Our Northwestern Mutual Private Client Group offers an exclusive, comprehensive approach to private wealth management. While we&#39;re the size of Fortune 500 company we have the specialized expertise of a boutique firm.{' '}
                  <Link
                    id='financial-planning-does-nm-offer-private-wealth-management-faq-link'
                    linkUrl='/private-wealth-management/'
                    variant='primaryDeemphasize'>
                    Find your Private Client Group advisor
                  </Link>.
                </Typography>
              ),
            },
          ]}
        />
      </Col>
    </Row>
  </SectionContainer>
);
